import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
  HttpResponse,
  HttpStatusCode,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { tap } from 'rxjs';
import { ToastService } from './toast.service';
import { Router } from '@angular/router';

type ResponseBody = {
  message?: unknown;
  success?: unknown;
  error?: unknown;
};

export const toastHttpInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
) => {
  const toast = inject(ToastService);
  const router = inject(Router);

  const authenticatedRequest = request.clone({
    withCredentials: true,
  });
  if (shouldIgnoreRequest(request)) {
    return next(authenticatedRequest);
  }

  return next(authenticatedRequest).pipe(
    tap({
      next: (event: HttpEvent<unknown>) => {

        if (!(event instanceof HttpResponse)) {
          return;
        }


        const body = event.body;

        if (shouldIgnoreResponse(request, event.body, event.headers.get('content-disposition'))) {
          return;
        }

        if (!isRecord(body)) {
          return;
        }

        const response = body as ResponseBody;

        if (typeof response.error === 'string' && response.error.trim()) {
          // toast.error(response.error.trim());
          return;
        }

        if (response.success === false) {
          const errorMessage = typeof response.message === 'string' ? response.message.trim() : '';

          // toast.error(errorMessage || 'Something went wrong');
          return;
        }

        if (typeof response.message === 'string') {
          const successMessage = response.message.trim();

          if (successMessage) {
            // toast.success(successMessage);
          }
        }
      },

      error: (error: unknown) => {
        if (shouldIgnoreRequest(request)) {
          return;
        }

        if (!(error instanceof HttpErrorResponse)) {
          // toast.error('Something went wrong');
          return;
        }

        if (error.status === HttpStatusCode.Forbidden) {
          toast.error("Session Ended", "Please log in again.");
          router.navigate(['/login']);
        }

        if (
          shouldIgnoreResponse(
            request,
            error.error,
            error.headers?.get('content-disposition') ?? null,
          )
        ) {
          return;
        }

        const payload = isRecord(error.error) ? (error.error as ResponseBody) : null;

        const message =
          asTrimmedString(payload?.message) ||
          asTrimmedString(payload?.error) ||
          asTrimmedString(error.message) ||
          'Something went wrong';
          
        // toast.error(message);
      },
    }),
  );
};

function shouldIgnoreRequest(request: HttpRequest<unknown>): boolean {
  const normalizedUrl = request.url.toLowerCase();
  return normalizedUrl.includes('/users/whoami');
}

function shouldIgnoreResponse(
  request: HttpRequest<unknown>,
  body: unknown,
  contentDisposition: string | null,
): boolean {
  if (request.responseType === 'blob' || request.responseType === 'arraybuffer') {
    return true;
  }

  if (body instanceof Blob || body instanceof ArrayBuffer) {
    return true;
  }

  const accept = request.headers.get('accept')?.toLowerCase() ?? '';
  if (
    accept.includes('application/octet-stream') ||
    accept.includes('application/pdf') ||
    accept.includes('image/') ||
    accept.includes('video/') ||
    accept.includes('audio/')
  ) {
    return true;
  }

  if (contentDisposition?.toLowerCase().includes('attachment')) {
    return true;
  }

  return false;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function asTrimmedString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}
